// Design Tokens for ELXR Sportsbook UI
export const tokens = {
  // Colors - Modern Crypto Theme
  colors: {
    // Primary palette - Electric Blue
    primary: {
      50: '#e6f4ff',
      100: '#bae3ff',
      200: '#7cc4fa',
      300: '#47a3f3',
      400: '#2186eb',
      500: '#0967d2',
      600: '#0552b5',
      700: '#03449e',
      800: '#01337a',
      900: '#002159'
    },
    
    // Secondary palette - Electric Purple/Pink
    secondary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f0d5ff',
      300: '#e0b3ff',
      400: '#c77dff',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7e22ce',
      800: '#6b21a8',
      900: '#581c87'
    },
    
    // Accent - Neon Green (for CTAs and success)
    accent: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d'
    },
    
    // Warning - Bright Yellow
    warning: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#713f12'
    },
    
    // Error - Vivid Red
    error: {
      50: '#fef2f2',
      100: '#fee2e2',
      200: '#fecaca',
      300: '#fca5a5',
      400: '#f87171',
      500: '#ef4444',
      600: '#dc2626',
      700: '#b91c1c',
      800: '#991b1b',
      900: '#7f1d1d'
    },
    
    // Neutral grays - Dark theme focused
    neutral: {
      0: '#ffffff',
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617'
    },
    
    // Dark backgrounds for crypto theme
    dark: {
      50: '#1a1f2e',
      100: '#151a27',
      200: '#111621',
      300: '#0d111c',
      400: '#090d17',
      500: '#060911',
      600: '#04060c',
      700: '#020408',
      800: '#010204',
      900: '#000000'
    },
    
    // Gradients - Updated for crypto theme
    gradients: {
      primary: 'linear-gradient(135deg, #0967d2 0%, #a855f7 100%)',
      secondary: 'linear-gradient(135deg, #a855f7 0%, #22c55e 100%)',
      accent: 'linear-gradient(135deg, #22c55e 0%, #0967d2 100%)',
      dark: 'linear-gradient(135deg, #0f172a 0%, #020617 100%)',
      glow: 'linear-gradient(135deg, #0967d2 0%, #a855f7 50%, #22c55e 100%)',
      card: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      hover: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)'
    }
  },
  
  // Typography - Keep existing
  typography: {
    fontFamily: {
      base: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      heading: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", Consolas, "Courier New", monospace'
    },
    
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
      '5xl': '3rem',    // 48px
      '6xl': '3.75rem'  // 60px
    },
    
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800
    },
    
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2
    }
  },
  
  // Spacing scale - Keep existing
  spacing: {
    0: '0',
    1: '0.25rem',   // 4px
    2: '0.5rem',    // 8px
    3: '0.75rem',   // 12px
    4: '1rem',      // 16px
    5: '1.25rem',   // 20px
    6: '1.5rem',    // 24px
    8: '2rem',      // 32px
    10: '2.5rem',   // 40px
    12: '3rem',     // 48px
    16: '4rem',     // 64px
    20: '5rem',     // 80px
    24: '6rem',     // 96px
    32: '8rem',     // 128px
    40: '10rem',    // 160px
    48: '12rem',    // 192px
    56: '14rem',    // 224px
    64: '16rem'     // 256px
  },
  
  // Border radius - Keep existing
  borderRadius: {
    none: '0',
    sm: '0.125rem',   // 2px
    base: '0.25rem',  // 4px
    md: '0.375rem',   // 6px
    lg: '0.5rem',     // 8px
    xl: '0.75rem',    // 12px
    '2xl': '1rem',    // 16px
    '3xl': '1.5rem',  // 24px
    full: '9999px'
  },
  
  // Shadows - Updated for dark theme
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    base: '0 1px 3px 0 rgba(0, 0, 0, 0.4), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.3)',
    glow: '0 0 20px rgba(34, 197, 94, 0.4)',
    'glow-primary': '0 0 20px rgba(9, 103, 210, 0.5)',
    'glow-secondary': '0 0 20px rgba(168, 85, 247, 0.5)',
    soft: '0 2px 15px rgba(0, 0, 0, 0.3)'
  },
  
  // Animations - Keep existing
  animations: {
    duration: {
      fast: '150ms',
      base: '200ms',
      slow: '300ms',
      slower: '400ms'
    },
    
    easing: {
      linear: 'linear',
      in: 'cubic-bezier(0.4, 0, 1, 1)',
      out: 'cubic-bezier(0, 0, 0.2, 1)',
      inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    }
  },
  
  // Breakpoints - Keep existing
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
  },
  
  // Z-index scale - Keep existing
  zIndex: {
    base: 0,
    dropdown: 10,
    sticky: 20,
    fixed: 30,
    modalBackdrop: 40,
    modal: 50,
    popover: 60,
    tooltip: 70,
    notification: 80
  }
};

// Helper functions
export const getColor = (path) => {
  const keys = path.split('.');
  let result = tokens.colors;
  for (const key of keys) {
    result = result[key];
  }
  return result;
};

export const getSpacing = (size) => tokens.spacing[size] || size;
export const getFontSize = (size) => tokens.typography.fontSize[size] || size;
export const getShadow = (size) => tokens.shadows[size] || tokens.shadows.base; 